import {
  createStyles,
  Fab,
  makeStyles,
  Typography,
  useMediaQuery,
  useTheme,
} from '@material-ui/core'
import { Add } from '@material-ui/icons'
import React, { useState } from 'react'
import {
  Datagrid,
  DeleteButton,
  FunctionField,
  List,
  ListProps,
  NumberField,
  NumberInput,
  Pagination,
  required,
  sanitizeListRestProps,
  SaveButton,
  SelectInput,
  SimpleForm,
  TextField,
  TextInput,
  Toolbar,
  TopToolbar,
  useListContext,
  useShowContext,
  useTranslate,
} from 'react-admin'
import { Link } from 'react-router-dom'
import { ResourceName } from '../../../core/ResourceName'
import Button from '../../common/customized-mui-components/Button'
import {
  CreateDialog,
  CreateDialogProps,
} from '../../common/customized-ra-components/CreateDialog'
import {
  EditDialog,
  EditDialogProps,
} from '../../common/customized-ra-components/EditDialog'
import { useHasAuthority } from '../../hooks/useHasAuthority'
import { Authority } from '../../../core/auth/Authority'
import { TextFieldWithMaxChars } from '../../common/TextFieldWithMaxChars'
import { PrintAnchor } from '../../../core/enum/PrintAnchor'
import { printBarcodeItemDto } from '../../../core/dto/print-barcode-item.dto'
import { PrintBarcodeDirection } from '../../../core/enum/PrintBarcodeDirection'
import { PrintBarcodeType } from '../../../core/enum/PrintBarcodeType'
import {
  ItemParametersGridExpand,
  printParameterTypeChoices,
} from './ItemParametersGridExpand'

const useStyles = makeStyles((theme: any) =>
  createStyles({
    editToolbar: {
      display: 'flex',
      '&>button:last-child': {
        marginLeft: 'auto',
      },
    },
    doubleInputDiv: {
      display: 'flex',
      gap: '16px',
      '& > *': {
        minWidth: '210px',
      },
    },
    tripleInputDiv: {
      display: 'flex',
      gap: '16px',
      '& > *': {
        minWidth: '134px',
      },
    },
    floating: {
      color: theme.palette.getContrastText(theme.palette.primary.main),
      backgroundColor: theme.palette.primary.main,
      margin: 0,
      top: 'auto',
      right: 20,
      bottom: 60,
      left: 'auto',
      position: 'fixed',
      zIndex: 1000,
    },
    smallScreenDialogCorrection: {
      marginTop: '210px',
    },
  }),
)

const validatePrintBarcodeItems = (values: printBarcodeItemDto) => {
  const errors: { [n: string]: string } = {}
  const requiredFields = [
    'xPosition',
    'yPosition',
    'anchor',
    'code',
    'priority',
    'barcodeDirection',
    'barcodeType',
    'height',
    'width',
  ] as const
  requiredFields.forEach((field) => {
    if (!values[field as keyof typeof values]) {
      errors[field] = 'ra.validation.required'
    }
  })
  return errors
}

const EditToolbar = ({ ...props }) => {
  const { handleClose, invalid, ...rest } = props
  const classes = useStyles()
  const hasAuthority = useHasAuthority()

  return (
    <Toolbar {...rest} className={classes.editToolbar}>
      <SaveButton disabled={invalid} redirect={false} onClick={handleClose} />
      <DeleteButton
        redirect={false}
        onClick={handleClose}
        disabled={!hasAuthority(Authority.DELETE_PRINT_BARCODE_ITEMS)}
      />
    </Toolbar>
  )
}

const BarcodeItemEditDialog = (props: EditDialogProps) => {
  const { handleClose, id: itemId } = props
  const classes = useStyles()
  const theme = useTheme()
  const smallScreen = useMediaQuery(theme.breakpoints.down('sm'))

  return (
    <EditDialog {...props} title={`ID ${itemId}`}>
      <SimpleForm
        redirect={false}
        toolbar={<EditToolbar handleClose={handleClose} />}
        validate={validatePrintBarcodeItems}
      >
        <TextInput
          source="code"
          validate={required()}
          fullWidth
          className={
            smallScreen ? classes.smallScreenDialogCorrection : undefined
          }
        />
        <div className={!smallScreen ? classes.tripleInputDiv : undefined}>
          <NumberInput
            resource={ResourceName.PRINT_BARCODE_ITEMS}
            source="xPosition"
            validate={required()}
            fullWidth
          />
          <NumberInput
            resource={ResourceName.PRINT_BARCODE_ITEMS}
            source="yPosition"
            validate={required()}
            fullWidth
          />
          <SelectInput
            source="anchor"
            resource={ResourceName.PRINT_BARCODE_ITEMS}
            fullWidth
            choices={[
              ...[
                PrintAnchor.TOP_LEFT,
                PrintAnchor.TOP_CENTER,
                PrintAnchor.TOP_RIGHT,
                PrintAnchor.CENTER_LEFT,
                PrintAnchor.CENTER,
                PrintAnchor.CENTER_RIGHT,
                PrintAnchor.BOTTOM_LEFT,
                PrintAnchor.BOTTOM_CENTER,
                PrintAnchor.BOTTOM_RIGHT,
              ].map((anchor) => ({
                id: anchor,
                name: `resources.enums.printAnchors.${anchor}`,
              })),
            ]}
          />
        </div>
        <div className={!smallScreen ? classes.doubleInputDiv : undefined}>
          <NumberInput
            resource={ResourceName.PRINT_BARCODE_ITEMS}
            source="height"
            validate={required()}
            fullWidth
          />
          <NumberInput
            resource={ResourceName.PRINT_BARCODE_ITEMS}
            source="width"
            validate={required()}
            fullWidth
          />
        </div>
        <div className={!smallScreen ? classes.doubleInputDiv : undefined}>
          <SelectInput
            source="barcodeDirection"
            resource={ResourceName.PRINT_BARCODE_ITEMS}
            fullWidth
            choices={[
              ...[
                PrintBarcodeDirection.LEFT_TO_RIGHT,
                PrintBarcodeDirection.RIGHT_TO_LEFT,
                PrintBarcodeDirection.BOTTOM_TO_TOP,
                PrintBarcodeDirection.TOP_TO_BOTTOM,
              ].map((direction) => ({
                id: direction,
                name: `resources.enums.printBarcodeDirections.${direction}`,
              })),
            ]}
          />
          <SelectInput
            source="barcodeType"
            resource={ResourceName.PRINT_BARCODE_ITEMS}
            fullWidth
            choices={[
              ...[
                PrintBarcodeType.CODE_2_OF_5_INTERLEAVED,
                PrintBarcodeType.CODE_3_OF_9_STANDARD,
                PrintBarcodeType.CODE_OMR,
              ].map((direction) => ({
                id: direction,
                name: `resources.enums.printBarcodeTypes.${direction}`,
              })),
            ]}
          />
        </div>
        <NumberInput source="priority" validate={required()} fullWidth />
      </SimpleForm>
    </EditDialog>
  )
}

const CreateToolbar = ({ ...props }) => {
  const { handleClose, invalid, ...rest } = props

  return (
    <Toolbar {...rest}>
      <SaveButton disabled={invalid} redirect={false} onClick={handleClose} />
    </Toolbar>
  )
}

const BarcodeItemCreateDialog = (props: CreateDialogProps) => {
  const { handleClose } = props
  const { record: printLayout } = useShowContext()
  const translate = useTranslate()
  const classes = useStyles()
  const theme = useTheme()
  const smallScreen = useMediaQuery(theme.breakpoints.down('sm'))

  return (
    <CreateDialog {...props}>
      <SimpleForm
        initialValues={{
          printLayoutId: printLayout?.id,
          anchor: PrintAnchor.TOP_LEFT,
          barcodeDirection: PrintBarcodeDirection.LEFT_TO_RIGHT,
          barcodeType: PrintBarcodeType.CODE_3_OF_9_STANDARD,
          priority: 1,
        }}
        redirect={false}
        toolbar={<CreateToolbar handleClose={handleClose} />}
        validate={validatePrintBarcodeItems}
      >
        <TextInput
          className={
            smallScreen ? classes.smallScreenDialogCorrection : undefined
          }
          source="code"
          validate={required()}
          fullWidth
        />
        <div className={!smallScreen ? classes.tripleInputDiv : undefined}>
          <NumberInput
            resource={ResourceName.PRINT_BARCODE_ITEMS}
            source="xPosition"
            validate={required()}
            fullWidth
          />
          <NumberInput
            resource={ResourceName.PRINT_BARCODE_ITEMS}
            source="yPosition"
            validate={required()}
            fullWidth
          />
          <SelectInput
            source="anchor"
            resource={ResourceName.PRINT_BARCODE_ITEMS}
            fullWidth
            choices={[
              ...[
                PrintAnchor.TOP_LEFT,
                PrintAnchor.TOP_CENTER,
                PrintAnchor.TOP_RIGHT,
                PrintAnchor.CENTER_LEFT,
                PrintAnchor.CENTER,
                PrintAnchor.CENTER_RIGHT,
                PrintAnchor.BOTTOM_LEFT,
                PrintAnchor.BOTTOM_CENTER,
                PrintAnchor.BOTTOM_RIGHT,
              ].map((anchor) => ({
                id: anchor,
                name: `resources.enums.printAnchors.${anchor}`,
              })),
            ]}
          />
        </div>
        <div className={!smallScreen ? classes.doubleInputDiv : undefined}>
          <NumberInput
            resource={ResourceName.PRINT_BARCODE_ITEMS}
            source="height"
            validate={required()}
            fullWidth
          />
          <NumberInput
            resource={ResourceName.PRINT_BARCODE_ITEMS}
            source="width"
            validate={required()}
            fullWidth
          />
        </div>
        <div className={!smallScreen ? classes.doubleInputDiv : undefined}>
          <SelectInput
            source="barcodeDirection"
            resource={ResourceName.PRINT_BARCODE_ITEMS}
            fullWidth
            choices={[
              ...[
                PrintBarcodeDirection.LEFT_TO_RIGHT,
                PrintBarcodeDirection.RIGHT_TO_LEFT,
                PrintBarcodeDirection.BOTTOM_TO_TOP,
                PrintBarcodeDirection.TOP_TO_BOTTOM,
              ].map((direction) => ({
                id: direction,
                name: `resources.enums.printBarcodeDirections.${direction}`,
              })),
            ]}
          />
          <SelectInput
            source="barcodeType"
            resource={ResourceName.PRINT_BARCODE_ITEMS}
            fullWidth
            choices={[
              ...[
                PrintBarcodeType.CODE_2_OF_5_INTERLEAVED,
                PrintBarcodeType.CODE_3_OF_9_STANDARD,
                PrintBarcodeType.CODE_OMR,
              ].map((direction) => ({
                id: direction,
                name: `resources.enums.printBarcodeTypes.${direction}`,
              })),
            ]}
          />
        </div>
        <NumberInput source="priority" validate={required()} fullWidth />
        <Typography>
          {translate('resources.print-item-parameters.titles.create-inline')}
        </Typography>
        <div className={!smallScreen ? classes.doubleInputDiv : undefined}>
          <TextInput
            label="resources.print-item-parameters.fields.key"
            source="newParamKey"
          />
          <TextInput
            label="resources.print-item-parameters.fields.options"
            source="newParamOptions"
          />
        </div>
        <SelectInput
          label="resources.print-item-parameters.fields.type"
          source="newParamType"
          choices={printParameterTypeChoices}
          fullWidth
        />
      </SimpleForm>
    </CreateDialog>
  )
}

const PrintBarcodeItemsActions = ({ ...props }) => {
  const { handleOpenCreate, ...rest } = props
  const translate = useTranslate()
  const hasAuthority = useHasAuthority()
  const classes = useStyles()
  const theme = useTheme()
  const smallScreen = useMediaQuery(theme.breakpoints.down('xs'))

  return (
    <TopToolbar {...sanitizeListRestProps(rest)}>
      <Button
        label={translate('ra.action.create')}
        onClick={handleOpenCreate}
        startIcon={<Add />}
        disabled={
          !hasAuthority(Authority.EDIT_PRINT_LAYOUTS) ||
          !hasAuthority(Authority.CREATE_PRINT_BARCODE_ITEMS)
        }
      />
      {smallScreen && (
        <Fab
          component={Link}
          to="#"
          className={classes.floating}
          onClick={handleOpenCreate}
        >
          <Add />
        </Fab>
      )}
    </TopToolbar>
  )
}

const PrintBarcodeItemsGrid = ({ ...props }) => {
  const { handleOpenEdit, ...rest } = props
  const translate = useTranslate()
  const theme = useTheme()
  const smallScreen = useMediaQuery(theme.breakpoints.down('sm'))
  const hasAuthority = useHasAuthority()

  return (
    <Datagrid
      {...rest}
      isRowExpandable={() => !smallScreen}
      expand={<ItemParametersGridExpand />}
      padding={smallScreen ? 'checkbox' : 'default'}
      hasBulkActions={false}
      rowClick={
        hasAuthority(Authority.EDIT_PRINT_BARCODE_ITEMS) &&
        hasAuthority(Authority.EDIT_PRINT_ITEM_PARAMETERS)
          ? handleOpenEdit
          : undefined
      }
      rowStyle={
        hasAuthority(Authority.EDIT_PRINT_BARCODE_ITEMS)
          ? () => ({
              color: 'default',
              cursor: 'pointer',
            })
          : undefined
      }
    >
      <TextField source="id" label="ID" />
      <TextFieldWithMaxChars source="code" maxChars={smallScreen ? 20 : 40} />
      {!smallScreen && <NumberField source="xPosition" />}
      {!smallScreen && <NumberField source="yPosition" />}
      {!smallScreen && (
        <FunctionField
          source="anchor"
          render={(record) =>
            translate(`resources.enums.printAnchors.${record?.anchor}`)
          }
        />
      )}
      {!smallScreen && <NumberField source="height" />}
      {!smallScreen && <NumberField source="width" />}
      {!smallScreen && (
        <FunctionField
          source="barcodeDirection"
          render={(record) =>
            translate(
              `resources.enums.printBarcodeDirections.${record?.barcodeDirection}`,
            )
          }
        />
      )}
      {!smallScreen && (
        <FunctionField
          source="barcodeType"
          render={(record) =>
            translate(
              `resources.enums.printBarcodeTypes.${record?.barcodeType}`,
            )
          }
        />
      )}
      {!smallScreen && <NumberField source="priority" />}
    </Datagrid>
  )
}

export const PrintBarcodeItemsList = (props: ListProps) => {
  const { basePath } = useListContext()
  const [createOpen, setCreateOpen] = useState<boolean>(false)
  const [editOpen, setEditOpen] = useState<boolean>(false)
  const [editId, setEditId] = useState<string | undefined>(undefined)
  const hasAuthority = useHasAuthority()

  const handleOpenCreate = () => {
    setCreateOpen(true)
  }

  const handleCloseCreate = () => {
    setCreateOpen(false)
  }

  const handleOpenEdit = (id: string) => {
    if (hasAuthority(Authority.EDIT_PRINT_BARCODE_ITEMS)) {
      setEditId(id)
      setEditOpen(true)
    }
  }

  const handleCloseEdit = () => {
    setEditOpen(false)
  }

  return (
    <>
      <BarcodeItemCreateDialog
        {...props}
        basePath={`/${ResourceName.PRINT_BARCODE_ITEMS}`}
        handleClose={handleCloseCreate}
        resource={ResourceName.PRINT_BARCODE_ITEMS}
        open={createOpen}
      />
      <BarcodeItemEditDialog
        {...props}
        basePath={`/${ResourceName.PRINT_BARCODE_ITEMS}`}
        handleClose={handleCloseEdit}
        id={editId as string}
        resource={ResourceName.PRINT_BARCODE_ITEMS}
        open={editOpen}
      />
      <List
        {...props}
        actions={
          <PrintBarcodeItemsActions handleOpenCreate={handleOpenCreate} />
        }
        basePath={basePath}
        bulkActionButtons={false}
        empty={false}
        exporter={false}
        pagination={<Pagination rowsPerPageOptions={[10, 20, 50, 100, 200]} />}
        perPage={20}
      >
        <PrintBarcodeItemsGrid handleOpenEdit={handleOpenEdit} />
      </List>
    </>
  )
}
